import React, { useEffect, useRef, useState } from "react";
import Header from "./Header/Header";
import Sidebar from "./Sidebar";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Drawer, Popper, ClickAwayListener, Paper, Typography, Grow } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  showSearchPopUP,
  toggleSideBar,
} from "../store/slices/ui-slice";
import useMediaQuery from "@mui/material/useMediaQuery";
import ElasticSearch from "../components/elastic-search/ElasticSearch";
import ExportLoader from "../components/Loader/ExportLoader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SensorsIcon from "@mui/icons-material/Sensors";
import sidebarIcons from "./Sidebar/sidebarIcons";
import { mono } from "../Theme/fonts";
import { toast } from "react-hot-toast";
import { URGENCY_CONFIG } from "../modules/LiveIntelligence/liveIntelData";

// Nav order — matched against item.link AND item.title (first match wins)
const NAV_ORDER = [
  "dashboard",
  "live",         // Live Intelligence
  "incident",     // Incidents
  "organization", // Organizations
  "data",         // Data Registries
  "report",       // Reporting / Reports
  "statistic",    // Statistics
  "person",       // Persons / Persons of Interest
  "notification", // Notifications
  "system",       // System Management — last
];

const getNavSortIndex = (item) => {
  const link = (item.link || "").toLowerCase();
  const title = (item.title || "").toLowerCase();
  const haystack = `${link} ${title}`;
  const idx = NAV_ORDER.findIndex((key) => haystack.includes(key));
  return idx === -1 ? NAV_ORDER.length : idx;
};

// ── Shared nav item style ──────────────────────────────────────────────────────
const itemSx = (active) => ({
  display: "flex",
  alignItems: "center",
  gap: { md: "5px", lg: "7px" },
  px: { md: "9px", lg: "13px", xl: "17px" },
  py: { md: "9px", lg: "10px" },
  cursor: "pointer",
  userSelect: "none",
  position: "relative",
  transition: "background 0.2s ease, border-color 0.2s ease",
  borderBottom: active ? "2.5px solid #ffffff" : "2.5px solid transparent",
  backgroundColor: active ? "rgba(255,255,255,0.15)" : "transparent",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    borderBottomColor: active ? "#ffffff" : "rgba(255,255,255,0.3)",
    "& .nav-icon": { opacity: 1 },
    "& .nav-label": { color: "#ffffff" },
    "& .nav-chevron": { color: "rgba(255,255,255,0.8)" },
  },
});

const iconSx = (active) => ({
  className: "nav-icon",
  component: "img",
  sx: {
    width: { md: "13px", lg: "14px", xl: "15px" },
    height: { md: "13px", lg: "14px", xl: "15px" },
    opacity: active ? 1 : 0.88,
    filter: "brightness(0) invert(1)",
    transition: "opacity 0.2s ease",
    flexShrink: 0,
  },
});

const labelSx = (active) => ({
  className: "nav-label",
  sx: {
    fontFamily: mono,
    fontSize: { md: "9px", lg: "10.5px", xl: "11.5px" },
    fontWeight: active ? 700 : 600,
    color: active ? "#ffffff" : "rgba(255,255,255,0.88)",
    letterSpacing: { md: "0.2px", lg: "0.4px" },
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    transition: "color 0.2s ease",
  },
});

// ── Vertical separator between nav sections ────────────────────────────────────
const NavDivider = () => (
  <Box sx={{
    width: "1px",
    my: "10px",
    mx: { md: "2px", lg: "4px" },
    backgroundColor: "rgba(255,255,255,0.2)",
    flexShrink: 0,
  }} />
);

const Layout = () => {
  const isTab = useMediaQuery("(max-width:900px)");
  const { showSideBar, exportLoading } = useSelector((state) => state.ui);
  const rawSidebar = useSelector((state) => state.auth.filteredSidebar);
  const filteredSidebar = [...(rawSidebar || [])].sort(
    (a, b) => getNavSortIndex(a) - getNavSortIndex(b)
  );
  const isLiveIntel = (item) =>
    (item.link || "").toLowerCase().includes("live") ||
    (item.title || "").toLowerCase().includes("live");
  const isDashboard = (item) =>
    (item.link || "").toLowerCase().includes("dashboard") ||
    (item.title || "").toLowerCase().includes("dashboard");
  const dashboardItems = filteredSidebar.filter(isDashboard);
  const otherNavItems = filteredSidebar.filter((item) => !isDashboard(item) && !isLiveIntel(item));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // ── Fire toast alerts for new FLASH / IMMEDIATE intel items ────────────────
  useEffect(() => {
    const handler = (e) => {
      const items = e.detail?.items || [];
      items.forEach((item) => {
        const urgency = item.urgency || "";
        if (urgency !== "FLASH" && urgency !== "IMMEDIATE") return;
        const cfg = URGENCY_CONFIG[urgency];
        toast(
          (t) => (
            <Box
              onClick={() => { toast.dismiss(t.id); navigate("/live-intelligence"); }}
              sx={{ cursor: "pointer", borderRadius: "8px", overflow: "hidden", width: "100%" }}
            >
              {/* Accent strip */}
              <Box sx={{ height: "3px", background: cfg.color, width: "100%" }} />

              <Box sx={{ p: "14px 16px" }}>
                {/* Badge row */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Box sx={{
                      px: "7px", py: "2px", borderRadius: "3px",
                      backgroundColor: cfg.color, color: "#fff",
                      fontFamily: mono, fontSize: "9px", fontWeight: 800, letterSpacing: "1.5px",
                    }}>
                      {urgency}
                    </Box>
                    <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 600, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>
                      {(item.type || "").replace(/_/g, " ")}
                    </Typography>
                  </Box>
                  {item.location?.country && (
                    <Typography sx={{ fontFamily: mono, fontSize: "9px", color: "#94A3B8" }}>
                      {item.location.country}
                    </Typography>
                  )}
                </Box>

                {/* Description */}
                <Typography sx={{ fontFamily: mono, fontSize: "12px", fontWeight: 600, color: "#0F172A", lineHeight: 1.5, mb: "10px" }}>
                  {item.description?.slice(0, 110)}{item.description?.length > 110 ? "…" : ""}
                </Typography>

                {/* Footer */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontFamily: mono, fontSize: "9px", color: "#94A3B8", letterSpacing: "0.3px" }}>
                    CT BUREAU // LIVE INTELLIGENCE
                  </Typography>
                  <Typography sx={{ fontFamily: mono, fontSize: "9px", fontWeight: 700, color: cfg.color, letterSpacing: "0.5px" }}>
                    VIEW FEED →
                  </Typography>
                </Box>
              </Box>
            </Box>
          ),
          {
            duration: urgency === "FLASH" ? 10000 : 6000,
            style: {
              padding: 0,
              background: "#ffffff",
              borderLeft: `4px solid ${cfg.color}`,
              boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
              borderRadius: "8px",
              maxWidth: "400px",
            },
          }
        );
      });
    };
    window.addEventListener("intel-feed-update", handler);
    return () => window.removeEventListener("intel-feed-update", handler);
  }, [navigate]);

  const { pathname: currentPath } = location;
  const [openDropdown, setOpenDropdown] = useState(null);
  const anchorRefs = useRef({});

  const handleSearchPopUp = () => {
    dispatch(showSearchPopUP());
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === "k") {
        event.preventDefault();
        handleSearchPopUp();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
  }, [currentPath]);

  const isActive = (item) => {
    if (item?.link && currentPath.includes(item.link)) return true;
    if (item?.Child_links) {
      return item.Child_links.some((child) => currentPath.includes(child.link));
    }
    return false;
  };

  const handleNavClick = (item, index) => {
    if (item?.Child_links?.length > 0) {
      setOpenDropdown((prev) => (prev === index ? null : index));
    } else if (item?.link) {
      setOpenDropdown(null);
      navigate(item.link);
    }
  };

  const handleChildClick = (child) => {
    setOpenDropdown(null);
    navigate(child.link);
  };

  const handleClickAway = (event) => {
    if (event.target.closest('[role="tooltip"]') || event.target.closest('.nav-dropdown-paper')) {
      return;
    }
    setOpenDropdown(null);
  };

  return (
    <>
      <header>
        <Header />
      </header>

      {/* ── Desktop Top Navigation Bar ── */}
      <Box
        sx={{
          position: "fixed",
          top: { xs: "56px", sm: "64px" },
          left: 0,
          right: 0,
          zIndex: 1099,
          background: "linear-gradient(180deg, #0085E5 0%, #0074cc 100%)",
          borderBottom: "1px solid #0068b8",
          boxShadow: "0 2px 12px rgba(0,133,229,0.3)",
          display: { xs: "none", md: "block" },
        }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              px: { md: "6px", lg: "10px", xl: "14px" },
              overflowX: "auto",
              "&::-webkit-scrollbar": { height: 0 },
            }}
          >
            {/* ── 1. DASHBOARD ── */}
            {dashboardItems.map((item, index) => {
              const active = isActive(item);
              return (
                <Box
                  key={`dash-${index}`}
                  ref={(el) => (anchorRefs.current[`dash-${index}`] = el)}
                  sx={{ position: "relative", display: "flex", alignItems: "stretch" }}
                  onClick={() => handleNavClick(item, `dash-${index}`)}
                >
                  <Box sx={itemSx(active)}>
                    <Box
                      {...iconSx(active)}
                      src={sidebarIcons[item?.dark_icon]}
                    />
                    <Typography {...labelSx(active)}>
                      {item.title}
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            <NavDivider />

            {/* ── 2. LIVE INTELLIGENCE ── */}
            {(() => {
              const active = currentPath.includes("/live-intelligence");
              return (
                <Box
                  sx={{ position: "relative", display: "flex", alignItems: "stretch" }}
                  onClick={() => navigate("/live-intelligence")}
                >
                  <Box sx={itemSx(active)}>
                    <SensorsIcon
                      className="nav-icon"
                      sx={{
                        fontSize: { md: "14px", lg: "15px", xl: "16px" },
                        color: "#FFFFFF",
                        opacity: active ? 1 : 0.5,
                        transition: "opacity 0.18s ease",
                        flexShrink: 0,
                      }}
                    />
                    <Typography {...labelSx(active)}>
                      Live Intel
                    </Typography>
                  </Box>
                </Box>
              );
            })()}

            <NavDivider />

            {/* ── 3. ALL OTHER NAV ITEMS ── */}
            {otherNavItems?.map((item, index) => {
              const active = isActive(item);
              const hasChildren = item?.Child_links?.length > 0;
              const isOpen = openDropdown === index;

              return (
                <Box
                  key={index}
                  ref={(el) => (anchorRefs.current[index] = el)}
                  sx={{ position: "relative", display: "flex", alignItems: "stretch" }}
                  onClick={() => handleNavClick(item, index)}
                >
                  <Box sx={itemSx(active)}>
                    <Box
                      {...iconSx(active)}
                      src={sidebarIcons[item?.dark_icon]}
                    />
                    <Typography {...labelSx(active)}>
                      {item.title}
                    </Typography>
                    {hasChildren && (
                      <ExpandMoreIcon
                        className="nav-chevron"
                        sx={{
                          fontSize: { md: 10, lg: 12 },
                          color: active ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.25)",
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.25s ease, color 0.18s ease",
                          ml: "-2px",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  {/* Dropdown */}
                  {hasChildren && (
                    <Popper
                      open={isOpen}
                      anchorEl={anchorRefs.current[index]}
                      placement="bottom-start"
                      style={{ zIndex: 1300 }}
                      transition
                      modifiers={[{ name: "offset", options: { offset: [0, 3] } }]}
                    >
                      {({ TransitionProps }) => (
                        <Grow
                          {...TransitionProps}
                          timeout={180}
                          style={{ transformOrigin: "top left" }}
                        >
                          <Paper
                            className="nav-dropdown-paper"
                            elevation={0}
                            sx={{
                              minWidth: "210px",
                              backgroundColor: "#ffffff",
                              border: "1px solid #e8eaef",
                              borderRadius: "10px",
                              boxShadow: "0 8px 30px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                              py: "6px",
                              overflow: "hidden",
                            }}
                          >
                            {item.Child_links?.filter(
                              (child) => child?.link !== "/network-graph"
                            )?.map((child, childIndex) => {
                              const childActive = currentPath.includes(child.link);
                              return (
                                <Box
                                  key={childIndex}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChildClick(child);
                                  }}
                                  sx={{
                                    px: "14px",
                                    py: "10px",
                                    mx: "6px",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    borderRadius: "6px",
                                    transition: "background 0.2s ease",
                                    backgroundColor: childActive ? "#f0f7ff" : "transparent",
                                    "&:hover": {
                                      backgroundColor: childActive ? "#e8f2ff" : "#f4f7fa",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      backgroundColor: childActive ? "#0085E5" : "#d1d5db",
                                      flexShrink: 0,
                                      transition: "background 0.2s ease",
                                      boxShadow: childActive ? "0 0 0 3px rgba(0,133,229,0.12)" : "none",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontFamily: mono,
                                      fontSize: "11px",
                                      fontWeight: childActive ? 700 : 500,
                                      color: childActive ? "#0085E5" : "#475569",
                                      whiteSpace: "nowrap",
                                      letterSpacing: "0.5px",
                                      transition: "color 0.2s ease",
                                    }}
                                  >
                                    {child.title}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  )}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Box>

      {/* Mobile/tablet: slide-in drawer */}
      {isTab && (
        <Drawer
          open={showSideBar}
          anchor="left"
          onClose={() => dispatch(toggleSideBar())}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "280px", sm: "300px" },
              top: { xs: "52px", sm: "56px" },
              height: { xs: "calc(100vh - 52px)", sm: "calc(100vh - 56px)" },
              borderRight: "1px solid #E2E8F0",
              boxShadow: "4px 0 20px rgba(0,0,0,0.08)",
              backgroundColor: "#FFFFFF",
            },
          }}
        >
          <Sidebar />
        </Drawer>
      )}

      {exportLoading && <ExportLoader />}

      {/* Main Content */}
      <Box
        pt="1.5rem"
        sx={{
          mt: { xs: "56px", sm: "64px", md: "106px" },
          px: { xs: "1rem", md: "1.5rem" },
          overflow: "hidden",
          minHeight: "calc(100vh - 130px)",
        }}
      >
        <Outlet />
      </Box>

      <ElasticSearch />
    </>
  );
};

export default Layout;
